import { create } from 'zustand';
import { Role, GroupedPermissions, RoleWithDetails } from '../types/role.types';
import { roleApi } from '../services/api/roleApi';
import logger from '../services/loggerService';

interface RoleState {
  roles: RoleWithDetails[];
  roleKeyMap: Map<string, RoleWithDetails>;
  permissions: GroupedPermissions;
  isLoadingRoles: boolean;
  isLoadingPermissions: boolean;
  fetchRoles: () => Promise<void>;
  fetchPermissions: () => Promise<void>;
  createRole: (name: string, description: string) => Promise<Role>;
  updateRolePermissions: (roleId: string, permissionIds: string[]) => Promise<void>;
  deleteRole: (roleId: string) => Promise<void>;
  getRoleById: (roleId: string) => RoleWithDetails | undefined;
  getRoleByKey: (roleKey: string) => RoleWithDetails | undefined;
}

// Lock to prevent concurrent fetch requests (Q23 Deduplication Guard)
let fetchRolesPromise: Promise<void> | null = null;
let fetchPermissionsPromise: Promise<void> | null = null;

export const useRoleStore = create<RoleState>((set, get) => ({
  roles: [],
  roleKeyMap: new Map(),
  permissions: {},
  isLoadingRoles: false,
  isLoadingPermissions: false,

  fetchRoles: async () => {
    // Deduplication guard: return existing promise if already fetching
    if (fetchRolesPromise) {
      return fetchRolesPromise;
    }

    fetchRolesPromise = (async () => {
      set({ isLoadingRoles: true });
      try {
        const roles = await roleApi.fetchRoles();

        // Create a map for easy lookup by role key (100% dynamic)
        const roleKeyMap = new Map<string, RoleWithDetails>();
        for (const role of roles) {
          if (role.key) {
            roleKeyMap.set(role.key, role);
          }
        }

        set({ roles, roleKeyMap, isLoadingRoles: false });
      } catch (error) {
        logger.error('Failed to fetch roles', error, 'useRoleStore.fetchRoles');
        set({ isLoadingRoles: false });
      } finally {
        fetchRolesPromise = null;
      }
    })();

    return fetchRolesPromise;
  },

  fetchPermissions: async () => {
    if (fetchPermissionsPromise) {
      return fetchPermissionsPromise;
    }

    fetchPermissionsPromise = (async () => {
      set({ isLoadingPermissions: true });
      try {
        const permissions = await roleApi.fetchPermissions();
        set({ permissions, isLoadingPermissions: false });
      } catch (error) {
        logger.error('Failed to fetch permissions', error, 'useRoleStore.fetchPermissions');
        set({ isLoadingPermissions: false });
      } finally {
        fetchPermissionsPromise = null;
      }
    })();

    return fetchPermissionsPromise;
  },

  createRole: async (name: string, description: string) => {
    const newRole = await roleApi.createRole(name, description);

    // Partial-success resilience: Event if fetchRoles fails (catch block inside it),
    // the creation operation remains successful and returns correctly.
    await get().fetchRoles();

    return newRole;
  },

  updateRolePermissions: async (roleId: string, permissionIds: string[]) => {
    // Security Boundary: Enforce strictly structured CUIDs before store-to-API transition
    if (!roleId || !/^c[a-z0-9]{24}$/.test(roleId)) {
      logger.error('Invalid role identifier in updateRolePermissions', { roleId }, 'useRoleStore.updateRolePermissions');
      throw new Error('Invalid role identifier format in store operation.');
    }
    if (permissionIds.some(pid => !pid || !/^c[a-z0-9]{24}$/.test(pid))) {
      logger.error('Invalid permission identifier in updateRolePermissions', { roleId, permissionIds }, 'useRoleStore.updateRolePermissions');
      throw new Error('Invalid permission identifier format in store operation.');
    }

    await roleApi.updateRolePermissions(roleId, permissionIds);
    await get().fetchRoles();
  },

  deleteRole: async (roleId: string) => {
    // Security Boundary: Enforce strictly structured CUIDs before store-to-API transition
    if (!roleId || !/^c[a-z0-9]{24}$/.test(roleId)) {
      logger.error('Invalid role identifier in deleteRole', { roleId }, 'useRoleStore.deleteRole');
      throw new Error('Invalid role identifier format in store operation.');
    }

    await roleApi.deleteRole(roleId);
    await get().fetchRoles();
  },

  getRoleById: (roleId: string) => {
    return get().roles.find((role) => role.id === roleId);
  },

  getRoleByKey: (roleKey: string) => {
    return get().roleKeyMap.get(roleKey);
  },
}));
